// SINGLE SOURCE OF TRUTH for sidebar modules.
// `key` MUST match the Sidebar's onTabChange(...) key. `capability` is the
// role-based gate (from CAPABILITIES). Add a row here when you add a sidebar
// item, and it appears automatically in the staff "Allowed modules" checklist.
//
// `addedAt` (epoch ms) is required on every module added from October 2026 on.
// It is what stops a new module from being invisible to existing staff: see
// moduleAllowed below.
export const MODULE_REGISTRY = [
    { key: 'dashboard', label: 'Dashboard', capability: 'canViewDashboard' },
    { key: 'parser', label: 'Parser', capability: 'canViewParser' },
    { key: 'orders', label: 'Orders', capability: 'canViewOrders' },
    { key: 'invoices', label: 'Invoices', capability: 'canViewFinance' },
    { key: 'labels', label: 'Labels', capability: 'canViewOrders' },
    { key: 'tracking', label: 'Shipping / Tracking', capability: 'canViewTracking' },
    { key: 'insights', label: 'Customer Insights', capability: 'canViewAnalytics' },
    { key: 'products', label: 'Products', capability: 'canViewProducts' },
    { key: 'customers', label: 'Customers', capability: 'canViewCustomers' },
    { key: 'telecaller', label: 'Telecaller Workspace', capability: 'canViewTelecallerWorkspace' },
    { key: 'complaints', label: 'Support / Complaints', capability: 'canViewComplaints' },
    { key: 'quick-order', label: 'Quick Order (Online)', capability: 'canViewComplaints' },
    { key: 'escalations', label: 'Courier Escalations', capability: 'canViewCourierEscalations' },
    { key: 'approvals', label: 'Approval Desk', capability: 'canViewApprovals' },
    { key: 'todo', label: 'To-Do', capability: 'canViewTodo' },
    { key: 'calendar', label: 'Calendar', capability: 'canViewCalendar' },
    { key: 'staff', label: 'Staff', capability: 'canViewStaff' },
    { key: 'chat', label: 'Chat', capability: 'canViewChat' },
    { key: 'analytics', label: 'Analytics', capability: 'canViewAnalytics' },
    { key: 'referrals', label: 'Referrals', capability: 'canViewReferrals' },
    { key: 'rewards', label: 'Rewards & Wallet', capability: 'canViewRewards', addedAt: Date.UTC(2026, 9, 10) },
    { key: 'handover', label: 'Handover Scan & Print', capability: 'canViewHandover', addedAt: Date.UTC(2026, 9, 12) },
    { key: 'qc', label: 'Parcel QC', capability: 'canViewQc', addedAt: Date.UTC(2026, 9, 24) },
    { key: 'settings', label: 'Settings', capability: 'canManageSettings' },
];

export const MODULE_KEYS = MODULE_REGISTRY.map(m => m.key);

const ADDED_AT = Object.fromEntries(MODULE_REGISTRY.filter(m => m.addedAt).map(m => [m.key, m.addedAt]));

// Per-user narrowing ON TOP OF role. allowedModules can only HIDE modules the
// role already allows — it can never grant beyond the role.
//
// Safety rules (so nobody is ever surprise-locked-out):
//  - no array / empty  → not restricted (full role access)
//  - any legacy '/path' entry → treat as legacy/unset → full role access
//    (existing staff were saved with '/dashboard'-style paths; those are ignored
//     until an admin re-saves them with the current key-based checklist)
//  - a SUPER ADMIN is never narrowed. They are the person who fixes a lockout;
//    hiding a module from them is how you lose access to the screen that
//    restores access.
//  - a module ADDED AFTER this user's checklist was saved is allowed. An allow
//    list is a record of what an admin was shown and chose from, so a module
//    that did not exist then was never declined — without this, every new
//    module ships invisible to every existing member of staff, which is exactly
//    how 'Rewards & Wallet' went missing for a super admin in October 2026.
//  - otherwise → enforce: show only if the key is listed
//
// @param {string[]} allowedModules  the user's saved checklist
// @param {string} key              module key
// @param {{savedAt?: number, role?: string}} [ctx]  when the checklist was saved, and the user's role
export const moduleAllowed = (allowedModules, key, ctx = {}) => {
    const { savedAt, role } = ctx;
    if (String(role || '').toLowerCase() === 'super_admin') return true;
    if (!Array.isArray(allowedModules) || allowedModules.length === 0) return true;
    if (allowedModules.some(x => String(x).startsWith('/'))) return true;
    if (allowedModules.includes(key)) return true;

    // Not listed — but was it even on offer when this list was saved?
    const addedAt = ADDED_AT[key];
    if (!addedAt) return false;                 // an original module, genuinely excluded
    return !savedAt || Number(savedAt) < addedAt;
};
